import { useFormikContext } from "formik";
import { motion } from "framer-motion";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

import ArrowUpIcon from "web/assets/icons/arrow-up.svg";

import useDropdown from "web/hooks/useDropdown";

import isArrayHasItems from "web/utils/data/validator/array/isArrayHasItems";
import getValueByStringPath from "web/utils/page/product/domesticTourism/getValueByStringPath";
import {
  getNextSelectIndex,
  isSelectCloseKey,
  isSelectCommitKey,
  isSelectNavKey,
  isSelectOpenKey,
} from "web/utils/ui/selectKeyboard";

import { Nullable } from "web/types/Utils";

import SelectItem from "./SelectItem";
import classes from "./select.scss";

interface ISelectProps {
  options: Nullable<string[] | { value: string; label: string; id?: string }[]>;
  className?: string;
  name: string;
  placeholder?: string;
  readonly: boolean;
}

const dropdownVariants = {
  open: {
    height: "auto",
  },
  closed: {
    height: 0,
  },
};
const transition = { duration: 0.25 };

const Select: FC<ISelectProps> = ({
  className = "",
  name,
  options,
  placeholder = "",
  readonly = false,
}) => {
  const [isOpen, setIsOpen] = useDropdown({
    scopeSelector: `[data-select-name="${name}"]`,
  });
  const { values, errors, touched, setFieldValue } =
    useFormikContext<Record<string, string | number>>();
  const isError =
    errors[name as keyof typeof errors] &&
    touched[name as keyof typeof touched];
  const buttonClass = isError ? classes.buttonError : classes.trigger;
  const classNameWrapper = isOpen ? classes.wrapperActive : classes.wrapper;
  const toggleHandler = useCallback(() => {
    setIsOpen((status) => !status);
  }, [setIsOpen]);

  const comboboxRef = useRef<HTMLButtonElement>(null);

  const closeHandler = useCallback(
    (event?: React.MouseEvent | React.KeyboardEvent) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      setIsOpen(false);
      comboboxRef.current?.focus();
    },
    [setIsOpen]
  );

  const valueActive = getValueByStringPath(values, name);

  const optionList = useMemo((): { value: string; label: string; id?: string }[] => {
    if (!isArrayHasItems(options)) return [];
    const first = options[0];
    if (typeof first === "string") {
      return (options as string[]).map((s) => ({ value: s, label: s }));
    }
    return options as { value: string; label: string; id?: string }[];
  }, [options]);

  const optionActive =
    values && optionList.find((option) => option.value === valueActive);
  const labelActive = optionActive && optionActive.label;
  const label = labelActive || placeholder;
  const labelClasses = labelActive
    ? classes.labelActive
    : classes.labelPlaceholder;

  const selectedIndex = useMemo(() => {
    const idx = optionList.findIndex((o) => o.value === valueActive);
    return idx >= 0 ? idx : 0;
  }, [optionList, valueActive]);

  const [activeIndex, setActiveIndex] = useState<number>(selectedIndex);

  useEffect(() => {
    if (!isOpen) return;
    setActiveIndex(selectedIndex);
  }, [isOpen, selectedIndex]);

  useEffect(() => {
    if (!isOpen) return;
    const option = optionList[activeIndex];
    if (!option) return;
    const el = document.getElementById(`${name}-option-${option.value}`);
    (el as HTMLElement | null)?.scrollIntoView?.({ block: "nearest" });
    (el as HTMLElement | null)?.focus?.();
  }, [activeIndex, isOpen, name, optionList]);

  return isArrayHasItems(options) && name ? (
    <div className={className || classes.root} data-select-name={name}>
      <div className={classNameWrapper}>
        <button
          disabled={readonly}
          type="button"
          className={buttonClass}
          onClick={toggleHandler}
          data-t1="select_options_input_button"
          role="combobox"
          aria-labelledby={name}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={`${name}-dropdown`}
          id={`${name}_combobox`}
          ref={comboboxRef}
          onKeyDown={(event) => {
            if (readonly || !isArrayHasItems(optionList)) return;
            const { key } = event;

            if (!isOpen && isSelectOpenKey(key)) {
              event.preventDefault();
              window.dispatchEvent(new Event("cafe:dropdown:closeAll"));
              setIsOpen(true);
              setActiveIndex(selectedIndex);
              return;
            }

            if (!isOpen) return;

            const currentIndex =
              activeIndex >= 0 && activeIndex < optionList.length
                ? activeIndex
                : selectedIndex;

            if (isSelectNavKey(key)) {
              event.preventDefault();
              const nextIndex =
                getNextSelectIndex({
                  key,
                  currentIndex,
                  count: optionList.length,
                }) ?? currentIndex;
              setActiveIndex(nextIndex);
              return;
            }

            if (isSelectCommitKey(key)) {
              event.preventDefault();
              const opt = optionList[activeIndex];
              if (opt) setFieldValue(name, opt.value);
              setIsOpen(false);
              comboboxRef.current?.focus();
              return;
            }

            if (isSelectCloseKey(key)) {
              event.preventDefault();
              setIsOpen(false);
              comboboxRef.current?.focus();
            }
          }}
        >
          <span className={labelClasses}>{label}</span>
          <ArrowUpIcon
            width={10}
            className={classes.arrow}
            dataT1="select_options_input_arrow_up_icon"
          />
        </button>
        <div className={classes.body}>
          <motion.div
            className={classes.dropdown}
            animate={isOpen ? "open" : "closed"}
            transition={transition}
            variants={dropdownVariants}
          >
            <div
              className={classes.content}
              id={`${name}-dropdown`}
              role="listbox"
              aria-labelledby={name}
            >
              {optionList.map((option, index) => (
                <SelectItem
                  name={name}
                  option={option}
                  key={`${name}-${option.id ?? option.value}`}
                  closeAction={closeHandler}
                  isOpen={isOpen}
                  optionIdPrefix={name}
                  index={index}
                  count={optionList.length}
                  activeIndex={activeIndex}
                  onRequestActiveIndexChange={(nextIndex: number) => {
                    const bounded = Math.max(
                      0,
                      Math.min(nextIndex, optionList.length - 1)
                    );
                    setActiveIndex(bounded);
                  }}
                  onRequestClose={() => closeHandler()}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  ) : null;
};

export default Select;
